import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EnergyConsumptionHelper, UnitsConversionHelper } from '@cfp/core';
import { ChartAbstract } from '@cfp/shared/ui/chart';
import { Card, Icon, Link } from '@cfp/ui';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ChartData, ChartDataset, ChartOptions, ChartType, LegendItem, PointStyle, TooltipItem } from 'chart.js';
import {
  BuildingDetailsDTO,
  ConstantsDTO,
  CountryCodeDTO,
  RegionCodeDTO,
  RoadmapBuildingDetailsDTO,
  RoadmapBuildingYearDTO
} from 'csr-asset-app';
import { BaseChartDirective } from 'ng2-charts';

interface NonElectricEnergySource {
  translationKey: string;
  unitText: string;
  needsSeparateAxis: boolean;
  getConvertedValue: (
    year: RoadmapBuildingYearDTO,
    constants: ConstantsDTO,
    countryCode: CountryCodeDTO,
    regionCode?: RegionCodeDTO
  ) => number;
}

@Component({
  selector: 'cfp-roadmap-chart-usage',
  templateUrl: './roadmap-chart-usage.html',
  imports: [Card, TranslateModule, BaseChartDirective, Link, Icon],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RoadmapChartUsage<
  TRoadmapDetails extends RoadmapBuildingDetailsDTO
> extends ChartAbstract<TRoadmapDetails> {
  readonly useDoubleYAxis = input<unknown>(false);
  protected readonly building = input.required<BuildingDetailsDTO>();
  protected readonly cumulative = signal<boolean>(false);
  protected readonly chartType: ChartType = 'line';
  protected readonly chartData = computed<ChartData | undefined>(() => {
    const details = this.details();
    const constants = this.constants();
    if (!details || !constants) {
      return undefined;
    }
    let yearDetails = details.years.filter((year: RoadmapBuildingYearDTO) => year.year);
    return {
      labels: yearDetails.map((year: RoadmapBuildingYearDTO) => {
        return year.year;
      }),
      datasets: this.createDatasets(yearDetails, constants)
    };
  });

  protected readonly chartOptions: ChartOptions = {
    responsive: true,
    resizeDelay: 200,
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          autoSkip: false
        }
      },
      yLeft: {
        beginAtZero: true,
        grid: {
          display: true
        },
        ticks: {
          callback: (value: any) => this.getFormattedValue(value)
        }
      }
    },
    maintainAspectRatio: false,
    elements: {
      point: {
        radius: 0
      }
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: (tooltipItem: TooltipItem<'line'>) => this.getFormattedValue(tooltipItem.raw),
          labelColor: (tooltipItem: TooltipItem<'line'>) => {
            const color = this.getDatasetColor(tooltipItem.datasetIndex);
            return {
              backgroundColor: this.getColorWithOpacity(color, '11'),
              borderColor: color
            };
          }
        }
      },
      legend: {
        display: true,
        position: 'bottom',
        align: 'start',
        labels: {
          usePointStyle: true,
          padding: 16,
          generateLabels: () => {
            return this.generateLabels();
          }
        }
      },
      datalabels: { display: () => false }
    }
  } as any;

  private constants = signal<ConstantsDTO | undefined>(undefined);
  private thirdColor: string;

  private decimalPipe = inject(DecimalPipe);
  private translateService = inject(TranslateService);
  private unitsConversionHelper = inject(UnitsConversionHelper);
  private energyConsumptionHelper = inject(EnergyConsumptionHelper);

  constructor() {
    super();

    const style = getComputedStyle(document.body);
    this.thirdColor = (style.getPropertyValue('--chart-third') || '#6c757d').trim();

    if (this.primaryColor === this.secondaryColor) {
      this.secondaryColor = this.getColorWithOpacity(this.secondaryColor, '22');
    }
    this.unitsConversionHelper
      .getConstants()
      .pipe(takeUntilDestroyed())
      .subscribe((constants) => {
        this.constants.set(constants);
      });
  }

  protected setCumulative(cumulative: boolean) {
    this.cumulative.set(cumulative);
  }

  protected getTranslation(key: string): string | undefined {
    const text = this.translateService.instant(key);
    if (text !== key) {
      return text;
    }

    return undefined;
  }

  protected getFormattedValue(value: any): string | undefined {
    if (value === 0) {
      return '-';
    }
    return this.decimalPipe.transform(value, '1.0-0') || undefined;
  }

  protected override formatTableLabel(label: string): string {
    return label
      .replace(` (${this.translateService.instant('chart.leftAxis')})`, '')
      .replace(` (${this.translateService.instant('chart.rightAxis')})`, '');
  }

  protected showData(): void {
    const chartData = this.chartData();
    if (!chartData) {
      return;
    }
    this.showDataEvent.emit(
      super.transformYearChartDataToTableData(
        chartData,
        this.translateService.instant('year'),
        this.translateService.instant('total.usage')
      )
    );
  }

  private getDatasetColor(datasetIndex: number): string {
    const colors = [this.primaryColor, this.secondaryColor, this.thirdColor];
    return colors[datasetIndex] ?? this.secondaryColor;
  }

  private createLabel(text: string, color: string, lineWidth: number = 2, pointStyle: PointStyle = 'line'): LegendItem {
    return {
      text: this.translateService.instant(text),
      fontColor: this.fontColor,
      lineWidth: lineWidth,
      strokeStyle: color,
      pointStyle: pointStyle
    };
  }

  private generateLabels(): LegendItem[] {
    const sources = this.getNonElectricEnergySources();
    const sourceColors = [this.secondaryColor, this.thirdColor];
    const labels: LegendItem[] = [];

    labels.push(this.createLabel(this.getElectricityLabel(sources), this.primaryColor));

    sources.forEach((source, index) => {
      let label = this.translateService.instant(source.translationKey, { unit: source.unitText });
      if (source.needsSeparateAxis) {
        label = `${label} (${this.translateService.instant('chart.rightAxis')})`;
      }
      labels.push(this.createLabel(label, sourceColors[index] ?? this.secondaryColor));
    });

    return labels;
  }

  private getElectricityLabel(sources: NonElectricEnergySource[]): string {
    let label = this.translateService.instant('usage.electricity');
    if (sources.some((source) => source.needsSeparateAxis)) {
      label = `${label} (${this.translateService.instant('chart.leftAxis')})`;
    }
    return label;
  }

  private getNonElectricEnergySources(): NonElectricEnergySource[] {
    const building = this.building();
    if (!building) {
      return [];
    }

    const heatingType = building.heatingConsumptionType;
    const hotWaterType = building.hotWaterType;
    const sources: NonElectricEnergySource[] = [];

    if (this.energyConsumptionHelper.usesGas(heatingType, hotWaterType)) {
      const unitText = this.unitsConversionHelper.gasUnitsText();
      sources.push({
        translationKey: 'usage.gas',
        unitText,
        needsSeparateAxis: unitText !== 'kWh',
        getConvertedValue: (year, constants, countryCode, regionCode) =>
          this.unitsConversionHelper.m3ToTargetGasUnits(year.totalGasConsumption, constants, countryCode, regionCode) ||
          0
      });
    }

    if (this.energyConsumptionHelper.usesDistrictHeating(heatingType, hotWaterType)) {
      const unitText = this.unitsConversionHelper.heatUnits();
      sources.push({
        translationKey: 'usage.heat',
        unitText,
        needsSeparateAxis: unitText !== 'kWh',
        getConvertedValue: (year, constants, countryCode, regionCode) =>
          this.unitsConversionHelper.GJToTargetHeatUnits(
            year.totalHeatConsumption,
            constants,
            countryCode,
            regionCode
          ) || 0
      });
    }

    if (this.energyConsumptionHelper.usesOil(heatingType, hotWaterType)) {
      const unitText = this.unitsConversionHelper.oilUnits();
      sources.push({
        translationKey: 'usage.oil',
        unitText,
        needsSeparateAxis: unitText !== 'kWh',
        getConvertedValue: (year, constants, countryCode, regionCode) =>
          this.unitsConversionHelper.literToTargetOilUnits(
            year.totalOilConsumption,
            constants,
            countryCode,
            regionCode
          ) || 0
      });
    }

    if (this.energyConsumptionHelper.usesWood(heatingType, hotWaterType)) {
      const unitText = this.unitsConversionHelper.woodUnitsText();
      sources.push({
        translationKey: 'usage.wood',
        unitText,
        needsSeparateAxis: unitText !== 'kWh',
        getConvertedValue: (year, constants, countryCode, regionCode) =>
          this.unitsConversionHelper.m3ToTargetWoodUnits(
            year.totalWoodConsumption,
            constants,
            countryCode,
            regionCode
          ) || 0
      });
    }

    if (this.energyConsumptionHelper.usesPellet(heatingType, hotWaterType)) {
      const unitText = this.unitsConversionHelper.pelletUnits();
      sources.push({
        translationKey: 'usage.pellet',
        unitText,
        needsSeparateAxis: unitText !== 'kWh',
        getConvertedValue: (year, constants, countryCode, regionCode) =>
          this.unitsConversionHelper.tonToTargetPelletUnits(
            year.totalPelletConsumption,
            constants,
            countryCode,
            regionCode
          ) || 0
      });
    }

    if (this.energyConsumptionHelper.usesCoal(heatingType, hotWaterType)) {
      const unitText = this.unitsConversionHelper.coalUnits();
      sources.push({
        translationKey: 'usage.coal',
        unitText,
        needsSeparateAxis: unitText !== 'kWh',
        getConvertedValue: (year, constants, countryCode, regionCode) =>
          this.unitsConversionHelper.tonToTargetCoalUnits(
            year.totalCoalConsumption,
            constants,
            countryCode,
            regionCode
          ) || 0
      });
    }

    return sources;
  }

  private createDataset(
    yAxisID: string,
    label: string,
    color: string,
    yearDetails: RoadmapBuildingYearDTO[],
    getYearValue: (year: RoadmapBuildingYearDTO) => number
  ): ChartDataset {
    let cumulativeValue = 0;
    return {
      yAxisID: yAxisID,
      label: label,
      backgroundColor: this.getColorWithOpacity(color, '11'),
      borderColor: color,
      borderWidth: 2,
      data: yearDetails.map((year) => {
        let value = getYearValue(year);
        if (this.cumulative()) {
          cumulativeValue += value;
          value = cumulativeValue;
        }
        return value;
      }),
      pointStyle: 'circle',
      pointBorderWidth: 1,
      pointBorderColor: color,
      pointBackgroundColor: color,
      pointHoverBorderWidth: 2,
      pointHoverBorderColor: color,
      pointHitRadius: 10,
      cubicInterpolationMode: 'monotone',
      fill: 'origin'
    };
  }

  private createDatasets(yearDetails: RoadmapBuildingYearDTO[], constants: ConstantsDTO): ChartDataset[] {
    const building = this.building();
    if (!building) return [];

    const energySources = this.getNonElectricEnergySources();
    const sourceColors = [this.secondaryColor, this.thirdColor];

    if (this.chartOptions.scales) {
      delete this.chartOptions.scales['yRight'];
      delete this.chartOptions.scales['yRight2'];
    }

    const datasets: ChartDataset[] = [
      this.createDataset(
        'yLeft',
        this.getElectricityLabel(energySources),
        this.primaryColor,
        yearDetails,
        (year) => year.totalElectricityConsumption
      )
    ];

    let rightAxisIndex = 0;
    energySources.forEach((source, index) => {
      let axisId = 'yLeft';

      if (source.needsSeparateAxis) {
        axisId = rightAxisIndex === 0 ? 'yRight' : 'yRight2';
        rightAxisIndex++;

        if (this.chartOptions.scales) {
          this.chartOptions.scales[axisId] = {
            position: 'right',
            beginAtZero: true,
            grid: { display: false },
            ticks: {
              callback: (value: any) => this.getFormattedValue(value)
            }
          };
        }
      }

      let label = this.translateService.instant(source.translationKey, { unit: source.unitText });
      if (source.needsSeparateAxis) {
        label = `${label} (${this.translateService.instant('chart.rightAxis')})`;
      }

      datasets.push(
        this.createDataset(axisId, label, sourceColors[index] ?? this.secondaryColor, yearDetails, (year) =>
          source.getConvertedValue(year, constants, building.address!.countryCode, building.address!.regionCode)
        )
      );
    });

    return datasets;
  }
}
